import { useEffect } from "react";
import Header from "@/components/Header";
import ProductGrid from "@/components/ProductGrid";
import FloatingFooter from "@/components/FloatingFooter";
import ConfirmationModal from "@/components/ConfirmationModal";
import HowItWorksModal from "@/components/HowItWorksModal";
import KioskInfo from "@/components/KioskInfo";
import PropertyKioskSelectionModal from "@/components/PropertyKioskSelectionModal";
import { useAppStore } from "@/store/appContext";
import { ShopifyService } from "@/utils/shopifyService";
import FirebaseService from "@/utils/firebaseService";
import { validateConfig, getCollectionId } from "@/utils/config";
import { useInactivityTimer } from "@/hooks/useInactivityTimer";
import { ShopifyProduct } from "@/types";
import "./App.css";

// Module-level promise to prevent duplicate API calls across React StrictMode remounts
let productsPromise: Promise<ShopifyProduct[]> | null = null;
// Track which property ID was used to load current products
let loadedPropertyId: string | null = null;

function App() {
  const {
    isLoading,
    error,
    products,
    setProducts,
    setLoading,
    setError,
    isHowItWorksModalOpen,
    closeHowItWorksModal,
    isPropertyKioskSelectionOpen,
    closePropertyKioskSelection,
    openPropertyKioskSelection,
    setSelectedPropertyKiosk,
    selectedProperty,
  } = useAppStore();

  // Initialize inactivity timer (2 minutes)
  useInactivityTimer(2);

  // Resolve property from shareable link (?property=<id>&kiosk=<name>)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const propertyId = params.get("property");
    const kioskName = params.get("kiosk");

    if (!propertyId || !kioskName) {
      return;
    }

    const resolvePropertyFromUrl = async () => {
      try {
        const firebaseService = FirebaseService.getInstance();
        const property = await firebaseService.getProperty(propertyId);

        if (!property) {
          openPropertyKioskSelection();
          return;
        }

        const syntheticKiosk = {
          id: kioskName,
          name: kioskName,
          propertyId: property.id,
          propertyName: property.name,
          active: true,
        };

        setSelectedPropertyKiosk(property, syntheticKiosk);
      } catch {
        openPropertyKioskSelection();
      }
    };

    resolvePropertyFromUrl();
  }, [setSelectedPropertyKiosk, openPropertyKioskSelection]);

  useEffect(() => {
    // Wait for property selection before loading products
    if (!selectedProperty || isPropertyKioskSelectionOpen) {
      return;
    }

    // Check if we need to reload products (property changed or no products loaded)
    const propertyChanged = loadedPropertyId !== selectedProperty.id;
    const needsReload = propertyChanged || products.length === 0;

    if (!needsReload) {
      return;
    }

    // Reset promise when property changes to allow reloading with new collection ID
    if (propertyChanged) {
      productsPromise = null;
      loadedPropertyId = null;
      // Clear existing products when property changes
      if (products.length > 0) {
        setProducts([]);
      }
    } else if (products.length === 0 && loadedPropertyId === selectedProperty.id) {
      // Products were cleared for refresh - reset promise to allow reload
      productsPromise = null;
    }

    // Prevent multiple API calls
    if (isLoading || productsPromise) {
      return;
    }

    const initializeApp = async () => {
      try {
        setLoading(true);

        // Get collection ID from selected property or fallback to env
        const collectionId = getCollectionId(selectedProperty);

        // Validate config with the resolved collection ID
        validateConfig(collectionId);

        const shopifyService = ShopifyService.getInstance();

        // Create the promise and store it globally with collection ID
        productsPromise = shopifyService.getProducts(collectionId);
        const fetchedProducts = await productsPromise;

        setProducts(fetchedProducts);
        loadedPropertyId = selectedProperty.id;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to initialize app"
        );
        // Reset the promise on error so retry is possible
        productsPromise = null;
        loadedPropertyId = null;
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, [
    selectedProperty,
    isPropertyKioskSelectionOpen,
    products.length,
    isLoading,
  ]); // Reload products when property changes

  return (
    <div className="app">
      <Header />
      <main className="main-content">
        <ProductGrid />
      </main>
      <FloatingFooter />
      <ConfirmationModal />
      <HowItWorksModal
        isOpen={isHowItWorksModalOpen}
        onClose={closeHowItWorksModal}
      />
      <PropertyKioskSelectionModal
        isOpen={isPropertyKioskSelectionOpen}
        onSelect={setSelectedPropertyKiosk}
        onClose={closePropertyKioskSelection}
      />
      <KioskInfo />

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner">Loading products...</div>
        </div>
      )}

      {error && (
        <div className="error-overlay">
          <div className="error-message">
            <h3>Error</h3>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
